import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from 'src/app/store.service';
import { UtilitiesService } from 'src/app/core/utilities.service';

@Component({
  selector: 'app-ai-landing-page',
  templateUrl: './ai-landing-page.component.html',
  styleUrls: ['./ai-landing-page.component.scss']
})
export class AiLandingPageComponent implements OnInit {

  @ViewChild('detailModal') detailModal!: TemplateRef<any>;
  @ViewChild('editPlanModal') editPlanModal!: TemplateRef<any>;

  historyList: any[] = [];
  isLoading = false;
  loadFailed = false;
  detail: any = null;
  isLoadingDetail = false;
  showApplyButton = false;
  isApplying = false;
  applyingType: number | null = null;
  editPlanItem: any = null;
  editPlanContent = '';
  isUpdatingPlan = false;
  isStoppingPlan = false;
  currentDetailId: string | null = null;
  comparisonId: string | null = null;
  isApply = 0;
  sameStore = false;
  planProgress: any = null;
  actionPlanOpen: { [key: string]: boolean } = {};

  searchTerm = '';

  page = 1;
  pageSize = 10;
  totalRecord = 0;

  get filteredList(): any[] {
    if (!this.searchTerm.trim()) return this.historyList;
    const term = this.searchTerm.trim().toLowerCase();
    return this.historyList.filter(item =>
      (item.own_page_url ?? '').toLowerCase().includes(term) ||
      (item.competitor_url ?? '').toLowerCase().includes(term)
    );
  }

  constructor(
    private svc: StoreService,
    private router: Router,
    private modalService: NgbModal,
    private utils: UtilitiesService
  ) { }

  ngOnInit(): void {
    this.loadList();
  }

  loadList(): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    const offset = (this.page - 1) * this.pageSize;
    this.isLoading = true;
    this.loadFailed = false;
    this.svc.getLandingPageList(bearer, storeId, this.pageSize, offset).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.historyList = res?.data ?? [];
        this.totalRecord = res?.total ?? res?.meta?.total ?? (res?.data?.length < this.pageSize ? offset + res?.data?.length : offset + this.pageSize + 1);
      },
      error: () => {
        this.isLoading = false;
        this.loadFailed = true;
      }
    });
  }

  changePage(page: number): void {
    this.page = page;
    this.loadList();
  }

  viewDetail(id: string): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    this.detail = null;
    this.showApplyButton = false;
    this.comparisonId = null;
    this.currentDetailId = id;
    this.isLoadingDetail = true;
    const modalRef = this.modalService.open(this.detailModal, { size: 'xl', scrollable: true, windowClass: 'modal-fullwidth' });
    modalRef.hidden.subscribe(() => this.loadList());
    this.svc.getLandingPageDetail(bearer, id, storeId).subscribe({
      next: (res: any) => {
        this.isLoadingDetail = false;
        const data = res?.data;
        this.comparisonId = data?.comparison_id ?? null;
        this.isApply = data?.is_apply ?? 0;
        this.planProgress = data?.plan_progress ?? null;
        const urlLookup = data?.url_lookup;
        this.sameStore = !!urlLookup?.same_store;
        this.showApplyButton = this.isApply == 0 && this.sameStore && urlLookup?.editor === 'html';
        this.detail = data?.analysis ?? data ?? res;
        this.normalizeDetail();
      },
      error: () => { this.isLoadingDetail = false; }
    });
  }

  onApply(apply: number): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    this.isApplying = true;
    this.applyingType = apply;
    this.svc.applyLandingPageUpdate(bearer, this.comparisonId ?? '', storeId, apply).subscribe({
      next: () => {
        this.isApplying = false;
        this.applyingType = null;
        this.showApplyButton = false;
        this.utils.msgBox.open({
          title: this.utils.translate('Success'),
          message: this.utils.translate('Your landing page has been optimised based on the AI recommendations.'),
          icon: 'success',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      },
      error: (err: any) => {
        this.isApplying = false;
        this.applyingType = null;
        const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to apply changes. Please try again.';
        this.utils.msgBox.open({
          title: this.utils.translate('Error'),
          message: this.utils.translate(msg),
          icon: 'error',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      }
    });
  }

  onApplyPlanItem(item: any): void {
    this.utils.msgBox.open({
      title: this.utils.translate('Apply Changes'),
      message: this.utils.translate('Are you sure you want to apply this change?'),
      icon: 'question',
      buttonList: [
        { type: 'spacing' },
        { text: this.utils.translate('Cancel'), style: 'secondary', onClick: 'close' },
        {
          text: this.utils.translate('Yes, Apply'),
          style: 'primary',
          onClick: () => this.doApplyPlanItem(item),
        },
      ],
    });
  }

  private doApplyPlanItem(item: any): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    item._applying = true;
    this.svc.manualApplyPlanItem(bearer, this.comparisonId ?? '', item.day, storeId).subscribe({
      next: () => {
        item._applying = false;
        this.utils.msgBox.open({
          title: this.utils.translate('Success'),
          message: this.utils.translate('Changes have been applied successfully.'),
          icon: 'success',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
        this.reloadDetail();
      },
      error: (err: any) => {
        item._applying = false;
        const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to apply. Please try again.';
        this.utils.msgBox.open({
          title: this.utils.translate('Error'),
          message: this.utils.translate(msg),
          icon: 'error',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      }
    });
  }

  private reloadDetail(): void {
    if (!this.currentDetailId) return;
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    this.isLoadingDetail = true;
    this.svc.getLandingPageDetail(bearer, this.currentDetailId, storeId).subscribe({
      next: (res: any) => {
        this.isLoadingDetail = false;
        const data = res?.data;
        this.comparisonId = data?.comparison_id ?? null;
        this.isApply = data?.is_apply ?? 0;
        this.planProgress = data?.plan_progress ?? null;
        const urlLookup = data?.url_lookup;
        this.sameStore = !!urlLookup?.same_store;
        this.showApplyButton = this.isApply == 0 && this.sameStore && urlLookup?.editor === 'html';
        this.detail = data?.analysis ?? data ?? res;
        this.normalizeDetail();
      },
      error: () => { this.isLoadingDetail = false; }
    });
  }

  onRetryDay(item: any): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    item._retrying = true;
    this.svc.retryDay(bearer, this.comparisonId ?? '', storeId, item.day).subscribe({
      next: () => {
        item._retrying = false;
        this.utils.msgBox.open({
          title: this.utils.translate('Success'),
          message: this.utils.translate('Changes have been applied successfully.'),
          icon: 'success',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
        this.reloadDetail();
      },
      error: (err: any) => {
        item._retrying = false;
        const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to retry. Please try again.';
        this.utils.msgBox.open({
          title: this.utils.translate('Error'),
          message: this.utils.translate(msg),
          icon: 'error',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      }
    });
  }

  onStopPlan(): void {
    this.utils.msgBox.open({
      title: this.utils.translate('Stop Auto Apply'),
      message: this.utils.translate('Are you sure you want to stop the auto apply plan?'),
      icon: 'question',
      buttonList: [
        { type: 'spacing' },
        { text: this.utils.translate('Cancel'), style: 'secondary', onClick: 'close' },
        {
          text: this.utils.translate('Yes, Stop'),
          style: 'danger',
          onClick: () => this.doStopPlan(),
        },
      ],
    });
  }

  private doStopPlan(): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';
    this.isStoppingPlan = true;
    this.svc.updateActionPlanStatus(bearer, this.comparisonId ?? '', 10, storeId).subscribe({
      next: () => {
        this.isStoppingPlan = false;
        this.isApply = 9;
        this.utils.msgBox.open({
          title: this.utils.translate('Success'),
          message: this.utils.translate('The auto apply plan has been stopped.'),
          icon: 'success',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      },
      error: (err: any) => {
        this.isStoppingPlan = false;
        const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to stop. Please try again.';
        this.utils.msgBox.open({
          title: this.utils.translate('Error'),
          message: this.utils.translate(msg),
          icon: 'error',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      }
    });
  }

  onSkipPlanItem(item: any): void {
    this.utils.msgBox.open({
      title: this.utils.translate('Skip Apply Changes'),
      message: this.utils.translate('Are you sure you want to skip this change?'),
      icon: 'question',
      buttonList: [
        { type: 'spacing' },
        { text: this.utils.translate('Cancel'), style: 'secondary', onClick: 'close' },
        {
          text: this.utils.translate('Yes, Skip'),
          style: 'danger',
          onClick: () => this.doSkipPlanItem(item),
        },
      ],
    });
  }

  private doSkipPlanItem(item: any): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    item._skipping = true;
    this.svc.updateActionPlan(bearer, this.comparisonId ?? '', item.day, { status: 9 }).subscribe({
      next: () => {
        item._skipping = false;
        item.status = 9;
        this.utils.msgBox.open({
          title: this.utils.translate('Success'),
          message: this.utils.translate('The change has been stopped successfully.'),
          icon: 'success',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      },
      error: (err: any) => {
        item._skipping = false;
        const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to skip. Please try again.';
        this.utils.msgBox.open({
          title: this.utils.translate('Error'),
          message: this.utils.translate(msg),
          icon: 'error',
          buttonList: [
            { type: 'spacing' },
            { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
          ],
        });
      }
    });
  }

  openEditPlan(item: any): void {
    this.editPlanItem = item;
    this.editPlanContent = item.new_content ?? '';
    this.modalService.open(this.editPlanModal, { size: 'md' });
  }

  onUpdatePlan(modal: any): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    this.isUpdatingPlan = true;
    this.svc.updateActionPlan(bearer, this.comparisonId ?? '', this.editPlanItem.day, { content: this.editPlanContent })
      .subscribe({
        next: () => {
          this.isUpdatingPlan = false;
          this.editPlanItem.new_content = this.editPlanContent;
          modal.close();
        },
        error: (err: any) => {
          this.isUpdatingPlan = false;
          const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to update. Please try again.';
          this.utils.msgBox.open({
            title: this.utils.translate('Error'),
            message: this.utils.translate(msg),
            icon: 'error',
            buttonList: [
              { type: 'spacing' },
              { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
            ],
          });
        }
      });
  }

  displayUrl(url?: string): string {
    if (!url) return '-';
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  private normalizeDetail(): void {
    if (!this.detail) return;
    const r = this.detail.readability;
    if (r?.dealer) {
      if (r.dealer.competitor && !r.competitor) {
        r.competitor = r.dealer.competitor;
        if (!r.recommendation) r.recommendation = r.dealer.competitor.recommendation;
      }
      if (r.dealer.action_plan && this.isEmptyOrMissing(this.detail.action_plan)) {
        const plan = r.dealer.action_plan;
        Object.values(plan).forEach((items: any) => {
          if (Array.isArray(items)) {
            items.forEach((item: any) => { if (item.status === undefined) item.status = 0; });
          }
        });
        this.detail.action_plan = plan;
      }
      if (r.dealer.rewritten_copy && !this.detail.rewritten_copy) {
        this.detail.rewritten_copy = r.dealer.rewritten_copy;
      }
    }
  }

  private isEmptyOrMissing(val: any): boolean {
    if (!val) return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'object') return Object.keys(val).length === 0;
    return false;
  }

  togglePhase(key: string): void {
    this.actionPlanOpen[key] = !this.actionPlanOpen[key];
  }

  getActionPlanPhases(actionPlan: any): { key: string; label: string; items: any[] }[] {
    if (!actionPlan) return [];
    return Object.entries(actionPlan).map(([key, items]) => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/(\d+)/g, ' $1').trim(),
      items: items as any[]
    }));
  }

  goToCompare(): void {
    this.router.navigate(['/dashboard/ai-landing-page-compare']);
  }
}
